import React from 'react';

import { formatNumber } from '@search/helpers/src/formatNumber';
import { useUserDetailsLoader } from '@search/auth/src/userDetails/userDetails';
import { useClassifiedOfferDataLayerPush } from '../../../../domain/offer/analytics/ClassifiedOfferGtmContext';
import { useRepairRequest } from './useRepairRequest';

export const useRepairUserForm = ({
    area = 30,
    place = 'modal'
}: {
    place?: 'modal' | 'gallery';
    area?: number;
}) => {
    const userInfoLoad = useUserDetailsLoader();
    const userInfo = userInfoLoad({});
    const dataLayerPush = useClassifiedOfferDataLayerPush();
    const [ state, setState ] = React.useState({
        isSending: false,
        isSent: false,
        error: undefined as Error | undefined,
        phone: userInfo.data?.userProfile.person?.primaryPhone?.number ?? '',
        isPhoneTouched: false
    });

    const url = typeof window !== 'undefined' ? window.location.href.replace(/\&/g, '%26') : '';

    const DEFAULT_REQUEST_ERROR_MESSAGE = 'Something went wrong';

    const setPhone = React.useCallback((phone: string) => {
        setState(prev => ({ ...prev, phone }));
    }, [ ]);

    React.useEffect(() => {
        if (place === 'gallery') {
            setPhone(userInfo.data?.userProfile.person?.primaryPhone?.number ?? '');
        }
    }, [ userInfo.data?.userProfile.person?.primaryPhone ]);

    const setIsSent = React.useCallback((error?: Error) => {
        setState(prev => ({
            ...prev,
            error,
            isSending: false,
            isSent: true
        }));
    });

    const isPhoneValid = React.useMemo(() => state.phone.length > 10, [ state.phone ]);

    // Цена ремонта ₽/м², пока константа далее возможно будет приходить из api
    const baseRepairPrice = 14900;

    const meterPrice = React.useMemo(() => {
        return formatNumber(baseRepairPrice);
    }, [ baseRepairPrice ]);
    const totalPrice = React.useMemo(() => {
        return formatNumber(Math.round(baseRepairPrice * area));
    }, [ baseRepairPrice ]);

    const sendRepairForm = useRepairRequest();
    const sendRequest = React.useCallback(() => {
        dataLayerPush({
            event: 'up_action',
            theme: 'remont',
            page_type: 'card',
            type: place === 'modal' ? 'card_count_req' : 'gallery_count_req'
        });

        sendRepairForm({ phone: state.phone, url, totalPrice })
            .then(({ data }) => {
                const errorMsg = data?.sendRepairContactForm?.errorText;

                if (errorMsg) {
                    setIsSent(new Error(errorMsg));
                } else {
                    setIsSent();
                }
            })
            .catch(() => {
                setIsSent(new Error(DEFAULT_REQUEST_ERROR_MESSAGE));
            });
    }, [ state, sendRepairForm ]);

    const resetForm = React.useCallback(() => {
        setState(prev => ({
            ...prev,
            isSent: false,
            error: undefined,
            isSending: false,
            isPhoneTouched: false
        }));
    }, [ ]);

    return {
        isSent: state.isSent,
        phone: state.phone,
        baseRepairPrice,
        isPhoneValid,
        sendRequest,
        meterPrice,
        totalPrice,
        resetForm,
        setPhone
    };
};
